// Helpers de présentation unifiée des notifications.
//
// Depuis Lot 4, toutes les "actions à suivre" (relances dossier, facture,
// proposition, contrat, échéances de tâches…) sont stockées avec
// `type = 'action_a_suivre'` et un `metadata.source` qui précise la nature.
//
// Les anciens types historiques (facture_impayee, prestation_a_relancer,
// tache_echeance…) restent supportés en lecture pour les notifications déjà
// créées en base.
import {
  AlertTriangle, UserPlus, MessageSquare, FileText, FileSignature, Receipt,
  ListTodo, Phone, Briefcase, Bell,
} from 'lucide-react';

export type NotifSource =
  | 'relance_dossier'
  | 'relance_facture'
  | 'relance_proposition'
  | 'relance_contrat'
  | 'tache_echeance'
  | 'acompte'
  | 'tache_assignee'
  | 'assignation'
  | 'mention'
  | 'point_critique';

interface NotifLike {
  type: string;
  metadata?: any;
}

/** Résout la "source" canonique d'une notification, qu'elle soit unifiée ou legacy. */
export function getNotifSource(n: NotifLike): NotifSource {
  if (n.type === 'action_a_suivre') {
    const s = n.metadata?.source as NotifSource | undefined;
    if (s) return s;
    return 'relance_dossier';
  }
  // Map legacy → source
  switch (n.type) {
    case 'facture_impayee':            return 'relance_facture';
    case 'prestation_a_relancer':      return 'relance_dossier';
    case 'proposition_sans_reponse':
    case 'devis_sans_reponse':         return 'relance_proposition';
    case 'contrat_non_signe':          return 'relance_contrat';
    case 'tache_echeance':             return 'tache_echeance';
    case 'tache_assignee':             return 'tache_assignee';
    case 'acompte_attente':            return 'acompte';
    case 'assignation':                return 'assignation';
    case 'mention':                    return 'mention';
    case 'point_critique':             return 'point_critique';
    default:                           return 'relance_dossier';
  }
}

const ICON_BY_SOURCE: Record<NotifSource, any> = {
  relance_dossier: Briefcase,
  relance_facture: Receipt,
  relance_proposition: FileText,
  relance_contrat: FileSignature,
  tache_echeance: AlertTriangle,
  tache_assignee: ListTodo,
  acompte: Receipt,
  assignation: UserPlus,
  mention: MessageSquare,
  point_critique: AlertTriangle,
};

const COLOR_BY_SOURCE: Record<NotifSource, { full: string; soft: string }> = {
  relance_dossier:     { full: 'text-amber-400 bg-amber-500/10 border-amber-500/20',   soft: 'text-amber-400 bg-amber-500/10' },
  relance_facture:     { full: 'text-red-400 bg-red-500/10 border-red-500/20',         soft: 'text-red-400 bg-red-500/10' },
  relance_proposition: { full: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', soft: 'text-yellow-400 bg-yellow-500/10' },
  relance_contrat:     { full: 'text-orange-400 bg-orange-500/10 border-orange-500/20', soft: 'text-orange-400 bg-orange-500/10' },
  tache_echeance:      { full: 'text-amber-400 bg-amber-500/10 border-amber-500/20',   soft: 'text-amber-400 bg-amber-500/10' },
  tache_assignee:      { full: 'text-blue-400 bg-blue-500/10 border-blue-500/20',      soft: 'text-blue-400 bg-blue-500/10' },
  acompte:             { full: 'text-pink-400 bg-pink-500/10 border-pink-500/20',      soft: 'text-pink-400 bg-pink-500/10' },
  assignation:         { full: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20', soft: 'text-emerald-400 bg-emerald-500/10' },
  mention:             { full: 'text-violet-400 bg-violet-500/10 border-violet-500/20', soft: 'text-violet-400 bg-violet-500/10' },
  point_critique:      { full: 'text-orange-400 bg-orange-500/10 border-orange-500/20', soft: 'text-orange-400 bg-orange-500/10' },
};

const LABEL_BY_SOURCE: Record<NotifSource, string> = {
  relance_dossier:     'Dossier à relancer',
  relance_facture:     'Facture à relancer',
  relance_proposition: 'Proposition à relancer',
  relance_contrat:     'Contrat à relancer',
  tache_echeance:      'Échéance tâche',
  tache_assignee:      'Tâche assignée',
  acompte:             'Acompte en attente',
  assignation:         'Assignation',
  mention:             'Mention',
  point_critique:      'Point critique',
};

export function getNotifIcon(n: NotifLike) {
  return ICON_BY_SOURCE[getNotifSource(n)] || Bell;
}
export function getNotifColor(n: NotifLike, variant: 'full' | 'soft' = 'full') {
  return COLOR_BY_SOURCE[getNotifSource(n)]?.[variant] || (variant === 'full' ? 'text-slate-400 bg-slate-500/10 border-slate-500/20' : 'text-slate-400 bg-slate-500/10');
}
export function getNotifLabel(n: NotifLike) {
  return LABEL_BY_SOURCE[getNotifSource(n)] || n.type;
}

export const NOTIF_SOURCE_OPTIONS: { value: NotifSource; label: string }[] =
  (Object.keys(LABEL_BY_SOURCE) as NotifSource[]).map((s) => ({ value: s, label: LABEL_BY_SOURCE[s] }));

/** Vrai si la notif représente une "action à suivre" (relance / échéance), qu'elle soit unifiée ou legacy. */
export function isActionASuivre(n: NotifLike): boolean {
  const s = getNotifSource(n);
  return s === 'relance_dossier' || s === 'relance_facture' || s === 'relance_proposition'
      || s === 'relance_contrat' || s === 'tache_echeance' || s === 'acompte';
}
